import store from '../store'
import ActionTypes from '../constants/actionTypes'
import { getVps } from '../modules/admin/actions'
import BatchPollingManager from './batchPollingManager'

// Drop an id the server keeps omitting after this many consecutive absences, so a
// deleted/inaccessible vp can't be re-fetched forever, while a transient gap is tolerated.
const MAX_CONSECUTIVE_MISSES = 5

// Shared poller for rows whose surfaces are still processing. Surface status isn't
// in the job-progress state (it's derived from whether the surface file paths exist),
// so it needs a game-dataset fetch. Collects all pending vps and refreshes them in
// one batched request per cycle instead of one GET /:id per row. Backs off on 429.
class SurfacePollingManager extends BatchPollingManager {
  constructor() {
    super()
    this.vpIds = new Set()
    // vpId -> count of consecutive cycles the server omitted this id.
    this.misses = new Map()
  }

  register(vpId) {
    this.vpIds.add(vpId)
    this.misses.delete(vpId)
    this._ensureTimer()
    this.scheduleImmediate()
  }

  unregister(vpId) {
    this._drop(vpId)
    if (this.vpIds.size === 0) {
      this._stopTimer()
    }
  }

  _drop(vpId) {
    this.vpIds.delete(vpId)
    this.misses.delete(vpId)
  }

  _hasWork() {
    return this.vpIds.size > 0
  }

  _collectIds() {
    return [...this.vpIds]
  }

  _fetch(chunk) {
    return getVps(chunk)
  }

  _handleData(data, ids) {
    // Iterate the ids we requested, not the response keys, so an id the server
    // never returns can't silently keep polling forever.
    ids.forEach(vpId => {
      const vp = data[vpId]
      if (!vp) {
        // The batch omitted this id: tolerate a transient gap, but give up once
        // it's been absent for MAX_CONSECUTIVE_MISSES cycles (vp deleted server-side).
        const misses = (this.misses.get(vpId) || 0) + 1
        if (misses >= MAX_CONSECUTIVE_MISSES) {
          this._drop(vpId)
        } else {
          this.misses.set(vpId, misses)
        }

        return
      }

      this.misses.delete(vpId)
      store.dispatch({ type: ActionTypes.UPDATE_TABLE_LIST_ITEM_SURFACES, payload: vp })

      // Stop polling once the vp has surfaces and every one has a path. A missing
      // or empty surfaces array means the shape isn't ready yet, so keep polling
      // rather than treating [].every() === true as "done".
      const surfaces = vp.files && vp.files.surfaces
      const surfacesReady =
        Array.isArray(surfaces) && surfaces.length > 0 && surfaces.every(surface => !!surface.path)
      if (surfacesReady) {
        this._drop(vpId)
      }
    })
  }

  _handleError(error) {
    const status = error && error.response && error.response.status
    // 429 is transient and the base already backed off. Surfaces have no error UI,
    // but log other failures so they aren't swallowed and re-tried invisibly.
    if (status !== 429) {
      console.error('SurfacePollingManager: surface batch poll failed', error)
    }
  }
}

export default new SurfacePollingManager()
